import React, { useMemo } from 'react';
import cn from 'classnames';
import { useField } from 'informed';

import { Option } from '../../../types';

import { ComboBox } from '../../ComboBox';
import { Select } from '../../Select';

import { FieldContainer } from './styledComponents';

export interface InformedPickerFieldProps {
  label: string;
  field: string;
  error: string;
  touched: boolean;
  isDisabled: boolean;
  validationSchemaObject: any;
  options: { label: string; value: string; [key: string]: any }[];
  placeholder: string;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  wrapperWithPopOverStyles?: string;
  popOverStyle?: string;
  fieldContainerStyles?: string;
  customOption?: (option: { label: string; value: string }) => React.ReactNode;
  inputGroupStyles?: any;
  buttonStyles?: any;
  inputFieldStyles?: string;
  labelStyles?: string;
  listBoxStyles?: string;
  headerClassName?: string;
  containerClassName?: string;
  wrapperComponent?: any;
  hasHeader?: boolean;
  hideCloseIcon?: boolean;
  reRenderObjects?: any[];
  helperText?: string;
  overlayOptions?: any;
  onChangeOption?: (value: string) => void;
  handleOnBlur?: () => void;
  helperTextStyles?: string;
  validateOnBlur?: boolean;
  isSearchable?: boolean;
  isMarginBottomZero?: boolean;
  errorMessageStyles?: string;
  enableDataMask?: boolean;
  handleFocus?: () => void;
}

const InformedPickerField = React.memo((props: InformedPickerFieldProps) => {
  const {
    label,
    field,
    error,
    touched,
    isDisabled,
    validationSchemaObject,
    isHidden,
    fieldWrapperStyles,
    wrapperWithPopOverStyles,
    fieldContainerStyles,
    inputGroupStyles,
    buttonStyles,
    options,
    placeholder,
    customOption,
    inputFieldStyles,
    labelStyles,
    listBoxStyles,
    headerClassName,
    containerClassName,
    wrapperComponent,
    hasHeader,
    hideCloseIcon,
    reRenderObjects,
    helperText,
    overlayOptions,
    onChangeOption,
    handleOnBlur,
    helperTextStyles,
    validateOnBlur = true,
    isSearchable = false,
    isMarginBottomZero,
    errorMessageStyles,
    enableDataMask,
    handleFocus
  } = props;

  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: true,
    // @ts-ignore - validationSchema is a runtime property
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    ...reRenderObjects
  });

  const { value } = fieldState;
  const { onChange, onBlur } = informed;
  const { setValue, setTouched } = fieldApi;

  // Convert options to Shadcn Option format
  const shadcnOptions: Option[] = useMemo(() => {
    return options.map((option) => ({
      label: option.label,
      value: String(option.value),
      disabled: option.disabled
    }));
  }, [options]);

  // Extract maxHeight from listBoxStyles (e.g., "max-h-162px" -> 162)
  const extractMaxHeight = (styles?: string): number | undefined => {
    if (!styles) return undefined;
    const maxHeightMatch = styles.match(/max-h-(\d+)px/);
    if (maxHeightMatch) {
      return parseInt(maxHeightMatch[1], 10);
    }
    // Default maxHeight if not specified
    return undefined;
  };

  const maxHeight =
    extractMaxHeight(listBoxStyles) ||
    extractMaxHeight(wrapperWithPopOverStyles) ||
    240;

  // Extract placement from overlayOptions if provided
  const placement = overlayOptions?.placement || 'bottom';
  const offset = overlayOptions?.offset || 4;

  const handleChange = (selectedValue: string) => {
    setValue(selectedValue);
    onChange(selectedValue);
    setTouched(true);
    onChangeOption && onChangeOption(selectedValue);
  };

  const handleBlur = () => {
    onBlur();
    handleOnBlur && handleOnBlur();
  };

  const handleOpenChange = (isOpen: boolean) => {
    // Call handleFocus when dropdown opens (similar to Omega Picker behavior)
    if (isOpen && handleFocus) {
      handleFocus();
    }
  };

  // Helper text is now passed to base components and handled internally
  // This aligns with InformedBranchCombobox and InformedPhoneNumberField patterns
  // BaseSelect and BaseSearchableDropdown use single-container pattern with inline color styles

  // Custom option renderer for BaseSearchableDropdown
  const renderOption = customOption
    ? (option: Option) =>
        customOption({ label: option.label, value: option.value })
    : undefined;

  // Combine className props
  const combinedClassName = cn(fieldContainerStyles, containerClassName);
  const combinedLabelClassName = labelStyles;

  // Base props for both BaseSelect and BaseSearchableDropdown
  const baseProps = {
    name: field,
    label: label,
    value: value?.toString(),
    placeholder: placeholder,
    options: shadcnOptions,
    onChange: handleChange,
    onBlur: handleBlur,
    error: error,
    helperText: helperText, // Pass helperText to base component for single-container pattern
    isDisabled: isDisabled,
    isRequired: false,
    className: combinedClassName,
    labelClassName: combinedLabelClassName,
    maxHeight: maxHeight,
    placement: placement as any,
    offset: offset,
    shouldCloseOnBlur: true,
    defaultOpen: false,
    enableAnimation: true,
    onOpenChange: handleOpenChange
  };

  const renderPickerField = () => {
    if (isSearchable) {
      return (
        <ComboBox
          {...baseProps}
          renderOption={renderOption}
          getOptionValue={(opt) => opt.value}
          getOptionLabel={(opt) => opt.label}
          inputClassName={inputFieldStyles}
          optionClassName={listBoxStyles}
          enableClientFilter={true}
        />
      );
    }

    return <Select {...baseProps} optionClassName={listBoxStyles} />;
  };

  return render(
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderPickerField()}
    </FieldContainer>
  );
});

InformedPickerField.displayName = 'InformedPickerField';

export { InformedPickerField };
